import React from "react";
import PropTypes from "prop-types";
import { Modal, message } from "antd";
import { AddressRecord, BankAccountRecord } from "records/company-record";
import AddAddressForm from "forms/add-address-form";
import AddAssociateBankAccountForm from "forms/add-associate-bank-account-form";
import {
    Card,
    Icon,
    ICONS,
    KeyValue,
    Pill,
    SectionHeading,
    EmptyState
} from "./ui";
import ApplicantCard from "./applicant-card";
import ApplicantVerification from "./applicant-verification";
import { money, resolvedDistrict } from "./data-utils";

/**
 * Case reference — applicants, company addresses and bank accounts.
 *
 * Everything on this panel is editable, through the write paths the existing
 * screens already use:
 *   - applicant fields → `associate-and-address-detail/` (Case Details ▸
 *     Applicants)
 *   - every address, applicant or company → `company/<id>/address/create-update/`
 *     via the shared AddAddressForm (Credit Checker ▸ Company Detail)
 *   - bank accounts → `updateCompanyBankAccounts` via the shared
 *     AddAssociateBankAccountForm (Credit Checker ▸ Bank Accounts)
 *
 * Reusing those forms rather than rebuilding them keeps validation, master
 * lookups and payload shaping identical to the rest of LOS.
 */

// AddressRecord's own keys — the API row carries extras (city_name, …) that the
// Record does not declare and `formValues` would not understand.
const ADDRESS_KEYS = [
    "id",
    "company",
    "associate",
    "address_type",
    "ownership_type",
    "residing_since",
    "address_line_1",
    "address_line_2",
    "city",
    "district",
    "state",
    "country",
    "pincode",
    "is_pv_done",
    "is_communication_address",
    "is_active"
];

const pickAddressFields = raw => {
    const out = {};
    if (!raw) return out;
    ADDRESS_KEYS.forEach(key => {
        if (raw[key] !== undefined) out[key] = raw[key];
    });
    return out;
};

export const addressSeed = raw => {
    if (!raw) return {};
    return { ...pickAddressFields(raw), district: resolvedDistrict(raw) };
};

// What "Same as residence address" carries over: the place, not the row.
const COPY_FROM_RESIDENCE = [
    "ownership_type",
    "residing_since",
    "address_line_1",
    "address_line_2",
    "pincode",
    "city",
    "district",
    "state",
    "country",
    "is_communication_address"
];

class CaseReferenceSection extends React.Component {
    state = {
        addressEditing: null,
        addressSaving: false,
        bankEditing: null,
        bankAdding: false
    };

    addressFormRef = React.createRef();
    bankFormRef = React.createRef();

    // One modal serves all address slots: the company's, and each applicant's
    // permanent / residence. Only the associate id and the type differ.
    openCompanyAddress = address =>
        this.setState({
            addressEditing: {
                raw: address.raw,
                addressType: address.addressType,
                associateId: null,
                title: address.title
            }
        });

    openApplicantAddress = (applicant, addressType, address) =>
        this.setState({
            addressEditing: {
                raw: address ? address.raw : null,
                // Source for the permanent form's "Same as residence address".
                residenceRaw:
                    addressType === 6 && applicant.residence ? applicant.residence.raw : null,
                seed: null,
                addressType,
                associateId: applicant.id,
                title: `${addressType === 6 ? "Permanent" : "Residence"} — ${applicant.name}`
            }
        });

    /**
     * AddAddressForm only reports the "Same as residence address" tick; the
     * host copies the values. They live in state (not setFieldsValue) because
     * the form re-seeds from `values` on every render.
     */
    onSameAsResidence = checked => {
        const editing = this.state.addressEditing;
        if (!editing) return;
        if (!checked) {
            this.setState({ addressEditing: { ...editing, seed: null } });
            return;
        }
        if (!editing.residenceRaw) {
            message.warning("Save this applicant's residence address first.");
            return;
        }
        const residence = addressSeed(editing.residenceRaw);
        const seed = { ...addressSeed(editing.raw) };
        COPY_FROM_RESIDENCE.forEach(key => {
            seed[key] = residence[key] === undefined ? null : residence[key];
        });
        this.setState({ addressEditing: { ...editing, seed } });
    };

    closeAddress = () =>
        this.setState({ addressEditing: null, addressSaving: false });

    saveAddress = () => {
        const form = this.addressFormRef.current;
        const editing = this.state.addressEditing;
        if (!form || !editing) return;
        form.validateFields((err, values) => {
            if (err) return;
            this.setState({ addressSaving: true });
            this.props.onSaveAddress(
                new AddressRecord(
                    Object.assign({}, pickAddressFields(values), {
                        // Carry the row's id so the endpoint updates it rather
                        // than creating a duplicate (null => create).
                        id: (editing.raw && editing.raw.id) || null,
                        company: this.props.companyId,
                        associate: editing.associateId,
                        address_type: editing.addressType
                    })
                ).formValues,
                saved => {
                    this.setState({ addressSaving: false });
                    if (saved) this.closeAddress();
                }
            );
        });
    };

    openBank = account =>
        this.setState({ bankEditing: account || null, bankAdding: !account });

    closeBank = () => this.setState({ bankEditing: null, bankAdding: false });

    saveBank = () => {
        const form = this.bankFormRef.current;
        if (!form) return;
        form.validateFields((err, values) => {
            if (err) return;
            const existing = this.state.bankEditing;
            const accounts = this.props.banks;
            this.props.onSaveBankAccount(
                new BankAccountRecord(
                    Object.assign(
                        {
                            id: existing ? existing.id : null,
                            company: this.props.companyId,
                            // First account on a company is the key account by
                            // definition; otherwise keep whatever it was.
                            is_key_account: !accounts.length
                                ? true
                                : existing
                                    ? existing.isKey
                                    : false
                        },
                        values,
                        {
                            associate:
                                values.associate === "company" ? null : values.associate
                        }
                    )
                ),
                saved => {
                    if (saved) this.closeBank();
                }
            );
        });
    };

    // Demote the old key account and promote the new one in a single write.
    setKeyAccount = account => {
        const current = this.props.banks.find(bank => bank.isKey);
        if (current && current.id === account.id) return;
        const records = [];
        if (current) records.push(current.record.set("is_key_account", false));
        records.push(account.record.set("is_key_account", true));
        this.props.onSaveBankAccount(records, () => {});
    };

    renderAddressModal() {
        const editing = this.state.addressEditing;
        return (
            <Modal
                visible={Boolean(editing)}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={760}
                title={
                    editing
                        ? `${editing.raw ? "Edit" : "Add"} ${editing.title} Address`
                        : ""
                }
                okText={"Save"}
                confirmLoading={this.state.addressSaving}
                onOk={this.saveAddress}
                onCancel={this.closeAddress}
            >
                {editing ? (
                    <AddAddressForm
                        ref={this.addressFormRef}
                        values={
                            new AddressRecord(
                                Object.assign({}, editing.seed || addressSeed(editing.raw), {
                                    company: this.props.companyId,
                                    associate: editing.associateId,
                                    address_type: editing.addressType
                                })
                            )
                        }
                        addressTypes={[editing.addressType]}
                        sameAddressCallback={this.onSameAsResidence}
                    />
                ) : null}
            </Modal>
        );
    }

    renderBankModal() {
        const { bankEditing, bankAdding } = this.state;
        const open = Boolean(bankEditing || bankAdding);
        return (
            <Modal
                visible={open}
                destroyOnClose
                className={"ops-ws-modal"}
                wrapClassName={"ops-ws-modal-wrap"}
                width={800}
                maskClosable
                title={bankEditing ? "Edit Bank Account" : "Add Bank Account"}
                okText={"Save"}
                confirmLoading={this.props.busy}
                onOk={this.saveBank}
                onCancel={this.closeBank}
            >
                {open ? (
                    <AddAssociateBankAccountForm
                        ref={this.bankFormRef}
                        values={bankEditing ? bankEditing.record : undefined}
                        associates={this.props.associateList}
                    />
                ) : null}
            </Modal>
        );
    }

    render() {
        const {
            applicants,
            addresses,
            banks,
            canEdit,
            busy,
            onSaveApplicant
        } = this.props;
        return (
            <div className={"ops-ws__section"}>
                <SectionHeading
                    title={"Case reference"}
                    subtitle={
                        "Applicants, addresses and bank — the context you validate against before triggering the kit."
                    }
                />

                {applicants.length ? (
                    <div className={"ops-ws__grid ops-ws__grid--wide"}>
                        {applicants.map((applicant, index) => (
                            <ApplicantCard
                                key={applicant.id}
                                applicant={applicant}
                                otherTypes={applicants
                                    .filter(other => other.id !== applicant.id)
                                    .map(other => other.type)}
                                index={index}
                                canEdit={canEdit}
                                busy={busy}
                                onSave={onSaveApplicant}
                                onEditAddress={this.openApplicantAddress}
                            />
                        ))}
                    </div>
                ) : (
                    <EmptyState>No applicants mapped to this case yet.</EmptyState>
                )}

                <ApplicantVerification
                    rows={this.props.verification}
                    documents={this.props.documents}
                    caseId={this.props.caseId}
                    loading={this.props.verificationLoading}
                    canEdit={canEdit}
                    onRefresh={this.props.onRefreshVerification}
                />

                {addresses.length ? (
                    <div className={"ops-ws__grid ops-ws__grid--narrow"}>
                        {addresses.map(address => (
                            <Card key={address.id} className={"ops-ws__card-pad"}>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 8
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", minWidth: 0 }}>
                                        <Icon d={ICONS.pin} size={16} stroke={"#1d4ed8"} />
                                        <span style={{ fontSize: 14.5, fontWeight: 800 }}>
                                            {address.title}
                                        </span>
                                        {address.ownership && address.ownership !== "—" ? (
                                            <Pill tone={"info"}>{address.ownership}</Pill>
                                        ) : null}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        <Pill tone={address.missingCount ? "warn" : "ok"}>
                                            {address.missingCount
                                                ? `${address.missingCount} field${address.missingCount > 1 ? "s" : ""} missing`
                                                : "Complete"}
                                        </Pill>
                                        <button
                                            type={"button"}
                                            className={"ops-ws__link"}
                                            disabled={!canEdit}
                                            onClick={() => this.openCompanyAddress(address)}
                                        >
                                            <Icon d={ICONS.pen} size={13} />
                                            Edit
                                        </button>
                                    </div>
                                </div>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "10px 16px",
                                        marginTop: 12
                                    }}
                                >
                                    {address.rows.map(row => (
                                        <KeyValue
                                            key={row.label}
                                            label={row.label}
                                            value={row.missing ? "Not captured" : row.value}
                                            tone={row.missing ? "missing" : undefined}
                                            span={row.span}
                                        />
                                    ))}
                                </div>
                            </Card>
                        ))}
                    </div>
                ) : null}

                <Card className={"ops-ws__card-pad"}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 14,
                            flexWrap: "wrap",
                            gap: 8
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                            <Icon d={ICONS.bank} size={17} stroke={"#1d4ed8"} />
                            <span style={{ fontSize: 14.5, fontWeight: 800 }}>Bank Accounts</span>
                            <span style={{ fontSize: 11, color: "#98a2b3", fontWeight: 600 }}>
                                {banks.length} account{banks.length === 1 ? "" : "s"} ·{" "}
                                {banks.filter(b => b.isKey).length} key tagged
                            </span>
                        </div>
                        <button
                            type={"button"}
                            className={"ops-ws__btn"}
                            disabled={!canEdit}
                            onClick={() => this.openBank(null)}
                        >
                            Add account
                        </button>
                    </div>
                    {banks.length ? (
                        <div className={"ops-ws__grid ops-ws__grid--narrow"}>
                            {banks.map(bank => (
                                <div
                                    key={bank.id}
                                    style={{
                                        padding: 14,
                                        borderRadius: 12,
                                        border: bank.isKey ? "1.5px solid #a6f4c5" : "1px solid #e6e8eb",
                                        background: bank.isKey ? "#f6fef9" : "#fff"
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            marginBottom: 10,
                                            gap: 8,
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                                            <span style={{ fontSize: 14, fontWeight: 800 }}>{bank.bank}</span>
                                            <span style={{ fontSize: 11, fontWeight: 600, color: "#667085" }}>
                                                {bank.typeLabel}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                            <Pill tone={bank.isKey ? "ok" : "neutral"}>
                                                {bank.isKey ? "Key · NACH reference" : "Secondary"}
                                            </Pill>
                                            {!bank.isKey ? (
                                                <button
                                                    type={"button"}
                                                    className={"ops-ws__link"}
                                                    disabled={!canEdit || busy}
                                                    onClick={() => this.setKeyAccount(bank)}
                                                >
                                                    Make key
                                                </button>
                                            ) : null}
                                            <button
                                                type={"button"}
                                                className={"ops-ws__link"}
                                                disabled={!canEdit}
                                                onClick={() => this.openBank(bank)}
                                            >
                                                <Icon d={ICONS.pen} size={13} />
                                                Edit
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 1fr",
                                            gap: "9px 16px"
                                        }}
                                    >
                                        <KeyValue label={"A/C Holder"} value={bank.holder} />
                                        <KeyValue label={"A/C Number"} value={bank.number} />
                                        <KeyValue label={"IFSC"} value={bank.ifsc} />
                                        <KeyValue label={"Branch"} value={bank.branch} />
                                        <KeyValue
                                            label={"A/C Limit"}
                                            value={bank.limit ? money(bank.limit) : null}
                                        />
                                        <KeyValue label={"A/C Type"} value={bank.typeLabel} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptyState>No bank accounts on file.</EmptyState>
                    )}
                </Card>

                {this.renderAddressModal()}
                {this.renderBankModal()}
            </div>
        );
    }
}

CaseReferenceSection.defaultProps = {
    verification: [],
    documents: [],
    onRefreshVerification: () => {}
};

CaseReferenceSection.propTypes = {
    applicants: PropTypes.array.isRequired,
    verification: PropTypes.array,
    verificationLoading: PropTypes.bool,
    onRefreshVerification: PropTypes.func,
    documents: PropTypes.array,
    caseId: PropTypes.number,
    addresses: PropTypes.array.isRequired,
    banks: PropTypes.array.isRequired,
    associateList: PropTypes.object,
    companyId: PropTypes.number,
    canEdit: PropTypes.bool,
    busy: PropTypes.bool,
    onSaveApplicant: PropTypes.func.isRequired,
    onSaveAddress: PropTypes.func.isRequired,
    onSaveBankAccount: PropTypes.func.isRequired
};

export default CaseReferenceSection;
